import {
  type ButtonHTMLAttributes,
  type ReactNode,
  useRef,
} from 'react';
import { useReducedMotion, useIsTouch } from '@/hooks/useScroll';

interface MagneticButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** How strongly the element follows the cursor (0–1). */
  strength?: number;
}

/**
 * Magnetic CTA — on desktop the button gently follows the pointer, then
 * springs back. Disabled on touch devices and when reduced-motion is set,
 * so it never harms accessibility or mobile performance.
 */
export function MagneticButton({
  children,
  strength = 0.35,
  className = '',
  ...props
}: MagneticButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const frame = useRef<number | null>(null);
  const reduced = useReducedMotion();
  const isTouch = useIsTouch();

  const apply = (x: number, y: number) => {
    const el = ref.current;
    if (el) el.style.transform = `translate(${x}px, ${y}px)`;
  };

  const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (reduced || isTouch) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    const x = relX * strength;
    const y = relY * strength;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => apply(x, y));
  };

  const reset = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    apply(0, 0);
  };

  return (
    <button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      className={`magnetic ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
